import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, XCircle } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, getAuthToken } from "@/lib/api";

/**
 * Iter 76 · Live System Updates Modal.
 *
 * Subscribes to the existing `/api/ws/chat` channel and intercepts any
 * `{ type: "new_system_update_released" }` frame. Pops a bilingual
 * modal with the release log + two action buttons:
 *   ✅ YES → POST /api/system-updates/flags/{slug} {enabled:true}
 *   ❌ NO  → dismiss only; flag stays at its current value.
 *
 * On mount we also fetch /api/system-updates/latest-release so a tenant
 * who was offline during the broadcast still sees the modal once when
 * they reconnect. We track shown-slugs in localStorage to avoid
 * re-popping the same release across page reloads.
 */
const SHOWN_KEY = "kf_sysupd_shown_v1";

function _readShown() {
  try { return JSON.parse(localStorage.getItem(SHOWN_KEY) || "{}") || {}; }
  catch { return {}; }
}
function _markShown(slug, broadcastAt) {
  try {
    const m = _readShown();
    m[slug] = broadcastAt || new Date().toISOString();
    localStorage.setItem(SHOWN_KEY, JSON.stringify(m));
  } catch { /* noop */ }
}

export default function SystemUpdatesModal() {
  const [release, setRelease] = useState(null);
  const [busy, setBusy] = useState(false);
  const wsRef = useRef(null);

  // Late-join replay: pull the latest persisted release once on mount.
  // Deferred until the AppLock + owner gates are both cleared so the
  // modal can't overlay sign-in inputs. Marker: a real JWT in storage.
  useEffect(() => {
    let alive = true;
    const timer = setTimeout(() => {
      const jwt = getAuthToken() || localStorage.getItem("kf_jwt") || "";
      if (!jwt) return; // unauthenticated → skip replay
      api.get("/system-updates/latest-release")
        .then(({ data }) => {
          if (!alive || !data?.release) return;
          const shown = _readShown();
          if (shown[data.release.slug] === data.release.broadcast_at) return;
          setRelease(data.release);
        })
        .catch(() => { /* fail-soft */ });
    }, 2500);
    return () => { alive = false; clearTimeout(timer); };
  }, []);

  // Live WS subscriber (mirrors EdiNotificationModal connection pattern).
  useEffect(() => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
    const jwt = getAuthToken() || localStorage.getItem("kf_jwt") || "";
    const url = jwt
      ? `${base}/api/ws/chat?auth=${encodeURIComponent(jwt)}`
      : `${base}/api/ws/chat?applock=savvaspos:store`;

    let alive = true;
    let backoff = 1000;
    function connect() {
      if (!alive) return;
      let ws;
      try { ws = new WebSocket(url); } catch { return; }
      wsRef.current = ws;
      ws.onopen = () => { backoff = 1000; };
      ws.onmessage = (ev) => {
        try {
          const frame = JSON.parse(ev.data);
          if (frame?.type === "new_system_update_released" && frame.slug) {
            const shown = _readShown();
            if (shown[frame.slug] === frame.broadcast_at) return;
            setRelease(frame);
          }
        } catch { /* malformed frame */ }
      };
      ws.onclose = () => {
        if (alive) {
          setTimeout(connect, backoff);
          backoff = Math.min(backoff * 2, 15000);
        }
      };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    }
    connect();
    return () => {
      alive = false;
      try { wsRef.current && wsRef.current.close(); } catch { /* noop */ }
    };
  }, []);

  const close = () => { if (!busy) setRelease(null); };

  const handleYes = async () => {
    if (!release?.slug) return;
    setBusy(true);
    try {
      await api.post(`/system-updates/flags/${release.slug}`, { enabled: true });
      toast.success("✅ Feature ενεργοποιήθηκε / activated");
      _markShown(release.slug, release.broadcast_at);
      setRelease(null);
      // Soft refresh — give the UI a moment to re-fetch feature gates.
      window.dispatchEvent(new CustomEvent("kf:feature-flags-changed"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Activation failed");
    } finally {
      setBusy(false);
    }
  };

  const handleNo = () => {
    if (!release?.slug) return;
    _markShown(release.slug, release.broadcast_at);
    setRelease(null);
    toast("⏸ Update kept hidden for this tenant");
  };

  if (!release) return null;

  return (
    <Dialog open onOpenChange={close}>
      <DialogContent
        data-testid="system-update-modal"
        className="max-w-lg border-2 border-fuchsia-400 bg-gradient-to-b from-slate-900 via-indigo-950 to-slate-950 text-slate-100 shadow-2xl"
      >
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-fuchsia-500/20 p-3 ring-2 ring-fuchsia-400/40">
            <Sparkles className="h-6 w-6 text-fuchsia-300" />
          </div>
          <div className="flex-1">
            <DialogTitle
              data-testid="system-update-modal-title"
              className="text-base font-bold leading-tight text-fuchsia-200"
            >
              🚀 NEW SYSTEM UPDATE RELEASED · ΝΕΑ ΑΝΑΒΑΘΜΙΣΗ
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-400 mt-0.5">
              Iter {release.iter} · Released {release.released_at}
            </DialogDescription>
          </div>
        </div>

        <div
          data-testid="system-update-modal-body"
          className="mt-4 space-y-2 rounded-lg bg-slate-800/60 p-4 ring-1 ring-slate-700"
        >
          <div className="text-sm font-bold text-amber-200" data-testid="system-update-title">
            {release.title}
          </div>
          {release.title_gr && (
            <div className="text-xs text-slate-300 italic">{release.title_gr}</div>
          )}
          <p className="text-sm text-slate-200 leading-relaxed mt-2" data-testid="system-update-summary">
            {release.summary}
          </p>
          {release.note && (
            <p className="text-[11px] text-fuchsia-200 mt-2 border-l-2 border-fuchsia-400 pl-2">
              {release.note}
            </p>
          )}
        </div>

        <div className="mt-5 flex flex-col sm:flex-row gap-2">
          <Button
            data-testid="system-update-yes-btn"
            disabled={busy}
            onClick={handleYes}
            className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold py-6"
          >
            <CheckCircle2 className="h-5 w-5 mr-2" />
            ✅ YES (Ενεργοποίηση)
          </Button>
          <Button
            data-testid="system-update-no-btn"
            disabled={busy}
            onClick={handleNo}
            variant="outline"
            className="flex-1 py-6 font-semibold border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700"
          >
            <XCircle className="h-5 w-5 mr-2" />
            ❌ NO (Όχι τώρα)
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
